// Library Methods
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";

// Components
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import ExamCardContainer from "../card-ui/ExamCardContainer/ExamCardContainer";
import ExamCardHeader from "../card-ui/ExamCardHeader/ExamCardHeader";
import ExamCardButton from "../card-ui/ExamCardButton/ExamCardButton";
import ExamCardToggles from "../card-ui/ExamCardToggles/ExamCardToggles";
import WhichEyeSection from "../card-ui/WhichEyeSection/WhichEyeSection";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";

// Types
import { SelectedExamCardType } from "src/services/exams/types";

// Constants
import {
  DEFAULT_STARTING_ROW_INDEX,
  DEFAULT_VISUAL_ACUITY_CARD,
  positionTypeEnums,
  visualAcuityRowOptions,
} from "src/constants/enums";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

type VisualAcuityCardType = {
  label: string;
  selectedExamCard: SelectedExamCardType | undefined;
  updateExamCardProperty: (
    propertyName: keyof SelectedExamCardType,
    value: SelectedExamCardType[keyof SelectedExamCardType]
  ) => void;
  onRemoveCard: () => void;
  onChangeEye: (value: string) => void;
  onChangeSectionOrder: (isRightFirst: boolean) => void;
};

const VisualAcuityCard = ({
  label,
  selectedExamCard,
  updateExamCardProperty,
  onChangeEye,
  onChangeSectionOrder,
  onRemoveCard,
}: VisualAcuityCardType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();

  const { NEAR, FAR } = positionTypeEnums;

  const examCardInfo = selectedExamCard ?? DEFAULT_VISUAL_ACUITY_CARD;

  const {
    startingRowIndex: initialRowIndex,
    examType,
    eyeCheckboxes,
    sectionOrder,
    optionalToggles,
    position,
    binocular,
  } = examCardInfo;

  const [selectedRowIndex, setSelectedRowIndex] = useState(
    initialRowIndex ?? DEFAULT_STARTING_ROW_INDEX
  );

  const canUseBaileyLovieExams = useMemo(
    () => clinicInfo?.licensing?.canUseBaileyLovieExams,
    [clinicInfo?.licensing?.canUseBaileyLovieExams]
  );

  const dropdownOptions = useMemo(
    () =>
      visualAcuityRowOptions.map((option) => ({
        displayName: option,
        value: option,
      })),
    []
  );

  const visualAcuityTypes = useMemo(
    () => [
      { type: "SNELLEN", label: "Snellen" },
      { type: "TUMBLING_E", label: "Tumbling E" },
      { type: "LANDOLT_C", label: "Landolt C" },
      { type: "ETDRS", label: "ETDRS" },
      ...(canUseBaileyLovieExams
        ? [{ type: "BAILEY_LOVIE", label: "Bailey Lovie" }]
        : []),
    ],
    [canUseBaileyLovieExams]
  );

  const visualAcuityPositions = useMemo(
    () => [
      { type: NEAR, label: t("Near") },
      { type: FAR, label: t("Far") },
    ],
    [t, NEAR, FAR]
  );

  const visualAcuityEyeOptions = useMemo(
    () => [
      { value: false, label: t("Monocular") },
      { value: true, label: t("Binocular") },
    ],
    [t]
  );

  const textStyle = {
    color: "#FFF",
    fontSize: "12px",
  };

  // A button only needs to wrap to a new line once its own label can't fit
  // -- not at some arbitrary shared width. "ch" scales with character
  // count, so each button's floor tracks its own text; +2ch covers the
  // button's horizontal padding/border.
  const buttonMinWidth = (buttonLabel: string) => `${buttonLabel.length + 2}ch`;

  return (
    <ExamCardContainer>
      <ExamCardHeader label={label} onRemoveCard={onRemoveCard} />
      <Grid
        item
        container
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        gap="8px"
        flexGrow={1}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          {/* Type */}
          <Grid
            item
            display="flex"
            flexDirection="column"
            gap="4px"
            flex="0 0 calc(50% - 4px)"
          >
            <TitleWithTooltip
              title={t("Type")}
              tooltip={t("More available soon.")}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />

            <Grid container item display="flex" flexWrap="wrap" gap="4px">
              {visualAcuityTypes.map((visualAcuityType) => (
                <Grid
                  item
                  xs
                  fontSize="12px"
                  minWidth={buttonMinWidth(visualAcuityType.label)}
                  key={visualAcuityType.type}
                >
                  <ExamCardButton
                    isSelected={examType === visualAcuityType.type}
                    updateValue={() =>
                      updateExamCardProperty("examType", visualAcuityType.type)
                    }
                    label={visualAcuityType.label}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Position */}
          <Grid
            item
            display="flex"
            flexDirection="column"
            gap="4px"
            flex="0 0 calc(50% - 4px)"
          >
            <TitleWithTooltip
              title={t("Position")}
              tooltip={t("More available soon.")}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />

            <Grid container item display="flex" flexWrap="wrap" gap="4px">
              {visualAcuityPositions.map((positionOption) => (
                <Grid
                  item
                  xs
                  fontSize="12px"
                  minWidth={buttonMinWidth(positionOption.label)}
                  key={positionOption.type}
                >
                  <ExamCardButton
                    isSelected={position === positionOption.type}
                    updateValue={() =>
                      updateExamCardProperty("position", positionOption.type)
                    }
                    label={positionOption.label}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Starting Row Index */}
          <Grid
            item
            display="flex"
            flexDirection="column"
            gap="4px"
            flex="0 0 calc(50% - 4px)"
          >
            <TitleWithTooltip
              title={t("Starting Row Index")}
              tooltip={t("Controls the exam's starting point.")}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />

            <SelectDropdown
              id="visual-acuity-starting-row-dropdown"
              height="27px"
              value={visualAcuityRowOptions[selectedRowIndex]}
              dropdownOptions={dropdownOptions}
              handleChange={(event) => {
                const selectedIndex = visualAcuityRowOptions.findIndex(
                  (val) => val === event.target.value
                );
                if (selectedIndex > -1) {
                  setSelectedRowIndex(selectedIndex);
                  updateExamCardProperty("startingRowIndex", selectedIndex);
                }
              }}
            />
          </Grid>

          {/* Eye */}
          <Grid
            item
            display="flex"
            flexDirection="column"
            gap="4px"
            flex="0 0 calc(50% - 4px)"
          >
            <TitleWithTooltip
              title={t("Eye")}
              tooltip={t(
                "Choose the binocular option to test both eyes at once or the monocular option to test one eye at a time."
              )}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />

            <Grid container item display="flex" flexWrap="wrap" gap="4px">
              {visualAcuityEyeOptions.map((option) => (
                <Grid
                  item
                  xs
                  fontSize="12px"
                  minWidth={buttonMinWidth(option.label)}
                  key={String(option.value)}
                >
                  <ExamCardButton
                    isSelected={option.value ? Boolean(binocular) : !binocular}
                    updateValue={() =>
                      updateExamCardProperty("binocular", option.value)
                    }
                    label={option.label}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <WhichEyeSection
            isRightFirst={sectionOrder?.right === 0}
            eyeCheckboxes={eyeCheckboxes}
            onChangeSectionOrder={onChangeSectionOrder}
            onChangeEye={onChangeEye}
            isHidden={binocular}
          />

          <Divider color="#5B6D7A" />

          <ExamCardToggles
            optionalToggles={optionalToggles}
            updateExamCardProperty={updateExamCardProperty}
            showToggles={DEFAULT_VISUAL_ACUITY_CARD.optionalToggles}
          />
        </Box>
      </Grid>
    </ExamCardContainer>
  );
};

export default VisualAcuityCard;
